import {
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, formatDistanceToNow, isPast, parseISO } from 'date-fns';
import { FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Toggle from 'react-toggle';
import Input from 'src/components/0100_input';
import Loading from 'src/components/0100_loading';
import ResponseBox from 'src/components/0100_response_box';
import AugmentedInput from 'src/components/0200_augmented_input';
import DatePicker from 'src/components/0200_datepicker';
import { updateEvent } from 'src/graphql/mutations/events.graphql';
import {
  IUpdateEventMutation,
  IUpdateEventMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import useEvent from 'src/hooks/events/useEvent';
import { useMutation } from 'urql';

interface IProps {
  canManageMod?: boolean;
  eventModSignupsCount: number;
  modSignupIsOpen: boolean;
  eventHasEnded: boolean;
}

const Basics: FC<IProps> = ({
  canManageMod,
  eventModSignupsCount,
  modSignupIsOpen,
  eventHasEnded,
}) => {
  const { event } = useEvent();
  const [isSuccessful, setIsSuccessful] = useState<keyof typeof dirtyFields>();
  const [isUpdating, setIsUpdating] = useState<keyof typeof dirtyFields>();
  const [error, setError] = useState<Record<string, string>>();
  const {
    register,
    reset,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      time: null as Date | null,
      limit: null as Number | null,
      enableWaitlist: true,
    },
  });
  const { time, limit, enableWaitlist } = watch();
  const [mutationResult, mutate] = useMutation<
    IUpdateEventMutation,
    IUpdateEventMutationVariables
  >(updateEvent);

  const handleUpdate = useCallback(
    ({
      time,
      limit,
      enableWaitlist,
      field,
    }: {
      time: Date | null;
      limit: Number | null;
      enableWaitlist: boolean;
      field: keyof typeof dirtyFields;
    }) => {
      if (Object.keys(dirtyFields).length === 0) return;
      setError(undefined);
      setIsSuccessful(undefined);
      setIsUpdating(field);

      mutate({
        eventId: Number(event?.id),
        modSignupOpensAt: dirtyFields.time
          ? time
            ? String(time)
            : null
          : undefined,
        modMaxSignup: dirtyFields.limit
          ? limit
            ? Number(limit)
            : null
          : undefined,
        modEnableWaitlist: dirtyFields.enableWaitlist
          ? enableWaitlist
          : undefined,
      }).then(res => {
        setIsUpdating(undefined);

        if (res.data?.updateEvent?.event?.id) {
          setIsSuccessful(field);
        }

        if (res.data?.updateEvent?.error) {
          setError({ [field]: res.data.updateEvent.error });
        }
      });
    },
    [event?.id, dirtyFields, mutate],
  );

  useEffect(() => {
    reset({
      time: event?.modSignupOpensAt ? parseISO(event.modSignupOpensAt) : null,
      limit: event?.modMaxSignup,
      enableWaitlist: event?.modEnableWaitlist ?? false,
    });
  }, [reset, event]);

  useEffect(() => {
    if (event?.id) {
      setError(undefined);
      setIsUpdating(undefined);
      setIsSuccessful(undefined);
    }
  }, [event?.id]);

  return (
    <div className="grid gap-4 w-full">
      <AugmentedInput
        title="Signup Open Time"
        footnote={
          !time &&
          canManageMod && (
            <div className="flex gap-2 items-center">
              <FontAwesomeIcon className="fa-fw" icon={faExclamationTriangle} />
              Players cannot signup to any Mods.
            </div>
          )
        }
      >
        {canManageMod ? (
          <div className="grid gap-4">
            <div className="flex gap-2 items-center">
              <DatePicker
                complex
                isClearable
                showTimeSelect
                date={time ?? undefined}
                dateFormat="yyyy MMM d hh:mm aa"
                maxDate={event?.startsAt ? parseISO(event.startsAt) : undefined}
                {...register('time')}
                onUpdate={time => {
                  setValue('time', time, { shouldDirty: true });
                  handleUpdate({ ...watch(), time, field: 'time' });
                }}
              />
              {mutationResult.fetching && isUpdating === 'time' && (
                <Loading size="small" />
              )}
              {isSuccessful === 'time' && <FontAwesomeIcon icon={faCheck} />}
            </div>

            {error?.time && (
              <ResponseBox type="error" withIcon={faExclamationTriangle}>
                {error.time}
              </ResponseBox>
            )}
          </div>
        ) : (
          <div className="pt-1.5">
            {modSignupIsOpen && !eventHasEnded && 'Open.'}
            {eventHasEnded && 'Game has ended.'}
            {time && !isPast(time) && (
              <span>
                {format(time, 'MMM dd hh:mm aa')} (
                {formatDistanceToNow(time, { addSuffix: true })})
              </span>
            )}
            {!time && 'Closed.'}
          </div>
        )}
      </AugmentedInput>
      <AugmentedInput title="Global Signup Limit">
        <div>
          {canManageMod ? (
            <div className="grid gap-2">
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  min={0}
                  {...register('limit', {
                    onBlur: () => {
                      handleUpdate({ ...watch(), limit, field: 'limit' });
                    },
                  })}
                  onFocus={e => e.target.select()}
                  className="text-right"
                />
                {mutationResult.fetching && isUpdating === 'limit' && (
                  <Loading size="small" />
                )}
                {isSuccessful === 'limit' && <FontAwesomeIcon icon={faCheck} />}
              </div>
              <div>Leave blank for unlimited signups.</div>
            </div>
          ) : (
            <div className="pt-1.5">
              {limit ? (
                <span>
                  <u className="underline-offset-4">{`${limit}`} Mods</u> for
                  this Game.
                </span>
              ) : (
                'Unlimited Mod Signups.'
              )}
            </div>
          )}
          {eventModSignupsCount === 0
            ? 'Currently not signed up to any Mods.'
            : `Currently signed up to ${eventModSignupsCount} Mods.`}
        </div>
      </AugmentedInput>
      <AugmentedInput title="Waitlist Allowed">
        {canManageMod ? (
          <div className="flex gap-2 items-center">
            <Toggle
              checked={enableWaitlist}
              onChange={x => {
                setValue('enableWaitlist', x.target.checked, {
                  shouldDirty: true,
                });
                handleUpdate({
                  ...watch(),
                  enableWaitlist: x.target.checked,
                  field: 'enableWaitlist',
                });
              }}
            />
            {mutationResult.fetching && isUpdating === 'enableWaitlist' && (
              <Loading size="small" />
            )}
            {isSuccessful === 'enableWaitlist' && (
              <FontAwesomeIcon icon={faCheck} />
            )}
          </div>
        ) : (
          <div className="pt-1.5">{enableWaitlist ? 'Yes.' : 'No.'}</div>
        )}
      </AugmentedInput>
    </div>
  );
};

export default Basics;
